/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  FAILED: 'failed',
  SUCCESS: 'success'
};

export default {
  name: 'requirement050',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      displayInfo: '',
      // ダイアログフラグ
      modal_requirement_050: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_050 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** データ読み出し処理 */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // データ読み出し
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：読み出しデータ表示
              this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_50_TXT_1`)
                .replace('$DID$', response.data.user_data.ssm_id ? `0x${response.data.user_data.ssm_id.toUpperCase()}` : '')
                .replace('$DID$', response.data.user_data.rom_id ? `0x${response.data.user_data.rom_id.toUpperCase()}` : '')
                .replace('$PROTOCOL$', response.data.user_data.protocol);
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_050 = false;
    },
  },

  async mounted() {
    this.init();
    // データ読み出し
    await this.read();
  },
};